import { ListContextProvider, ReduxState, useListContext } from "ra-core"
import { FC, useMemo } from "react"
import { Link as RouterLink } from 'react-router-dom'
import { Content } from "@/components/content/Content"
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditIcon from '@mui/icons-material/Edit';
import { OdResourcePageProps } from "@/types/resource"
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import { Card, Box, IconButton, Tooltip, Typography } from "@mui/material"
import { Datagrid } from "@/components/list/datagrid"
import Scrollbar from "@/components/Scrollbar"
import { RecPatch } from "@/dataProvider/tools"
import { DatabaseOwnershipStatsDto } from "@/gen/api/governance"
import useLocalListController from "@/components/list/useLocalListController"
import { FunctionField, TextField } from "@/components/field"
import { genUrlWithHashid } from "@/utils/hashid"
import { AtopFuncArea, FilterField, FilterOption, SearchField } from "@/components/list/datagridFunctionWidgets";
import { useSelector } from "react-redux";
import DatabaseNameField from "@/components/databases/DatabaseNameField";
import { EncryptedDatabasesContextProvider } from "@/components/databases/useEncryptedDatabases";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { EDIT_DATA_OWNERSHIP } from "@/constants";


const listOptions : {sorters: Record<string, (a:DatabaseOwnershipStatsDto & RecPatch, b:DatabaseOwnershipStatsDto & RecPatch) => number>} = {
  sorters: {
    id: (a, b) => a.id < b.id ? -1 : 1,
    name: (a, b) => (a.name || '') < (b.name || '') ? -1 : 1,
  }
}

const DataOwnership: FC<OdResourcePageProps> = (props) => {
  // useResourceRegister({ name: resource })
  const listCtrl = useLocalListController({ resource: '', basePath: '', filter: {} }, listOptions)

  return (
    <ListContextProvider value={listCtrl}>
      <DataOwnershipBody {...props} />
    </ListContextProvider>
  )
}


const DataOwnershipBody: FC<OdResourcePageProps> = ({
  options,
  resource,
}) => {
  const hasWritePermit = useHasPermit([EDIT_DATA_OWNERSHIP])
  const listCtx = useListContext<DatabaseOwnershipStatsDto & RecPatch>()
  
  const data = useSelector<ReduxState, Record<number, DatabaseOwnershipStatsDto>>(store => store.admin.resources[resource as string].data as any)
  const dbList = Object.values(data || {});

  const engineOptions = useMemo(() => Object.values(
    dbList.reduce((acc, d) => {
      const label = d.engine || ''
      const option = ({
        label,
        value: label,
      })
      acc[label] = option

      return acc
    }, {} as Record<string, {label:string,value:string}>)
  ).sort((a, b) => a.label < b.label ? -1 : 1), [ JSON.stringify([...new Set(dbList.map(d => d.engine))].sort()) ])
  

  return (
    <Content pageName="Data Ownership" resourceName="Data Ownership" breadcrumbs={options?.breadcrumbs}>
      <EncryptedDatabasesContextProvider>
        <Card>
          <AtopFuncArea>
            <SearchField 
              sourceKey="name"
              match={(d:DatabaseOwnershipStatsDto, fval) => !!d.name && d.name.toLowerCase().includes((fval as string).trim().toLowerCase())}
              sx={{ width: 280 }}
              TextFieldProps={{ placeholder: 'Search by Database Name' }}
            />
            <FilterField 
              label="Engine" 
              filterOptions={engineOptions} 
              sourceKey="engine"
              match={(d: DatabaseOwnershipStatsDto, fval:FilterOption|'_empty_') => {
                if (fval === '_empty_') return true
                const value = fval.value
                return d.engine === value
              }}
              sx={{ width: 320 }}
            />
          </AtopFuncArea>
          <Scrollbar options={{ suppressScrollY: true }}>
            <Box sx={{ minWidth: 1150 }}>
              <Datagrid 
                hasBulkActions={false} 
                sx={{ 
                  '& th' : { py: 2 },
                  '& td' : { py: 2 },
                }}
                {...listCtx}
              >
                <DatabaseNameField label="Database" />
                <TextField source="engine" label="Engine" sortable={false} />
                <FunctionField 
                  source="" 
                  label="Owned Tables Count" 
                  sortable={false} 
                  render={(d?:DatabaseOwnershipStatsDto & RecPatch) => (
                    <Typography 
                      component="span" 
                      variant="h6" 
                      color={(d?.ownedCount || 0) === 0 ? 'error.main' : ((d?.ownedCount || 0) < 5) ? 'warning.main' : 'success.main' }
                    >
                      {d?.ownedCount || 0}
                      <Typography component="span" variant="body2" sx={{ ml: 1, color: "text.primary" }}>
                        / {d?.totalCount || 0}
                      </Typography>
                    </Typography>
                  )}
                />
                <FunctionField 
                  label="Action"
                  sortable={false}
                  render={(d?: DatabaseOwnershipStatsDto & RecPatch) => (
                    <Tooltip title={hasWritePermit ? "Edit" : "View"}>
                      <IconButton 
                        sx={{ 
                          color: 'text.primary' as any, 
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                        component={RouterLink}
                        to={genUrlWithHashid(`/governance/data-ownership/$0/show`, d?.databaseId || 0, d?.name)}
                      >
                        {hasWritePermit ? <EditIcon fontSize="small" /> : <ArrowForwardIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  )}
                />
              </Datagrid>
            </Box>
          </Scrollbar>
          {//@ts-ignore 
          <DefaultPagination {...listCtx} />}
        </Card>
      </EncryptedDatabasesContextProvider>
    </Content>
  )
}

export default DataOwnership;
